import React, { useState } from 'react';
import { ADDON_CLEANUP_REGEX } from '../../utils/addonRegex';
import { cleanDescriptionHtml } from '../../utils/cleanHtml';
import { getSpecialtyCategory, getSpecialtyColor, validOfferedDurations } from '../../utils/categories';

const hexToRgba = (hex, alpha) => {
  if (!hex) return undefined;
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
  const n = parseInt(full, 16);
  if (Number.isNaN(n)) return undefined;
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
};

function CopyableContact({ value, icon }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    if (!value) return;
    try {
      navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) { /* clipboard unavailable */ }
  };
  return (
    <button
      type="button"
      onClick={handleCopy}
      className="flex items-center gap-2 w-full text-left group hover:bg-[#314e4a]/10 rounded px-1 py-0.5 transition-colors"
      title="Click to copy"
    >
      {icon}
      <span className="text-[#314e4a] font-medium break-all flex-1">{value}</span>
      <span className={`text-xs font-medium flex-shrink-0 ${copied ? 'text-green-600' : 'text-gray-400 group-hover:text-[#314e4a]'}`}>
        {copied ? 'Copied!' : 'Copy'}
      </span>
    </button>
  );
}

export default function PractitionerCard({
  selectedPractitioner,
  activeServices,
  categories,
  specialties,
  primaryOfferedServices,
  addonOfferedServices,
  activePracTab,
  setActivePracTab,
  setSelectedCategory,
  setSelectedService,
  setMainBgColor,
  adminMode,
  handleEditPractitioner,
  playClickSound,
  defaults,
  DAYS,
  getShiftStyles,
}) {
  return (
    <div className="relative bg-white rounded-xl border text-gray-700 shadow-lg overflow-hidden">
      {adminMode && (
        <button
          onClick={() => handleEditPractitioner(selectedPractitioner)}
          className="absolute top-4 right-4 p-2 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors z-10 shadow-sm"
          aria-label="Edit Practitioner"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-600" viewBox="0 0 20 20" fill="currentColor">
            <path d="M17.414 2.586a2 2 0 00-2.828 0L7 10.172V13h2.828l7.586-7.586a2 2 0 000-2.828z" />
            <path fillRule="evenodd" d="M2 6a2 2 0 012-2h4a1 1 0 010 2H4v10h10v-4a1 1 0 112 0v4a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" clipRule="evenodd" />
          </svg>
        </button>
      )}

      <div className="p-6 border-b bg-gradient-to-r from-gray-50 to-white">
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <h3 className="text-2xl font-bold text-gray-900 mb-1">{selectedPractitioner.name}</h3>
            <div className="flex items-center gap-2">
              {selectedPractitioner.specialty && (() => {
                // Built-in specialties keep their Tailwind pill styling;
                // admin-added ones get a tinted pill from their swatch color.
                const builtInClass = selectedPractitioner.specialty === 'Massage Therapist' ? 'bg-green-50 text-green-700' :
                  selectedPractitioner.specialty === 'Acupuncturist' ? 'bg-blue-50 text-blue-700' :
                    selectedPractitioner.specialty === 'Esthetician' ? 'bg-pink-50 text-pink-700' : null;
                const swatch = builtInClass ? undefined : getSpecialtyColor(specialties, selectedPractitioner.specialty);
                const tint = swatch ? hexToRgba(swatch, 0.18) : undefined;
                return (
                  <span className={`px-3 py-1 rounded-full text-base font-medium ${builtInClass || (tint ? 'text-gray-800' : 'bg-teal-50 text-teal-700')}`} style={tint ? { backgroundColor: tint, borderLeft: `3px solid ${swatch}` } : undefined}>
                    {selectedPractitioner.specialty}
                  </span>
                );
              })()}
              {selectedPractitioner.title && (
                <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-base font-medium">
                  {selectedPractitioner.title}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="flex border-b bg-white">
        <button
          onClick={() => { playClickSound(); setActivePracTab('bio'); }}
          className={`flex-1 py-2 px-3 font-medium text-center transition-all duration-200 text-sm ${activePracTab === 'bio'
            ? 'bg-teal-50 border-b-2 border-teal-500 text-teal-700'
            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
            }`}
        >
          <span className="flex items-center justify-center gap-1">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            Bio
          </span>
        </button>
        <button
          onClick={() => { playClickSound(); setActivePracTab('services'); }}
          className={`flex-1 py-2 px-3 font-medium text-center transition-all duration-200 text-sm ${activePracTab === 'services'
            ? 'bg-teal-50 border-b-2 border-teal-500 text-teal-700'
            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
            }`}
        >
          <span className="flex items-center justify-center gap-1">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            Services
          </span>
        </button>
        <button
          onClick={() => { playClickSound(); setActivePracTab('info'); }}
          className={`flex-1 py-2 px-3 font-medium text-center transition-all duration-200 text-sm ${activePracTab === 'info'
            ? 'bg-teal-50 border-b-2 border-teal-500 text-teal-700'
            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
            }`}
        >
          <span className="flex items-center justify-center gap-1">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            Schedule & Info
          </span>
        </button>
      </div>

      <div className="p-3">
        {activePracTab === 'info' && (
          <div className="space-y-4">
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-2">Weekly Schedule:</h4>
              {selectedPractitioner.isByRequest ? (
                <div className="bg-[#314e4a]/10 border border-[#314e4a]/20 rounded-lg p-4 mb-2">
                  <div className="flex items-center gap-2 mb-3">
                    <h5 className="font-semibold text-[#314e4a]">Available by Request Only</h5>
                  </div>
                  <div className="text-sm text-gray-700 ml-0 space-y-2">
                    <p className="text-gray-600 text-xs italic mb-2">Please reach out to this practitioner directly to schedule an appointment.</p>
                    {selectedPractitioner.contactPhone && (
                      <CopyableContact
                        value={selectedPractitioner.contactPhone}
                        icon={
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-[#314e4a]/70 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                          </svg>
                        }
                      />
                    )}
                    {selectedPractitioner.contactEmail && (
                      <CopyableContact
                        value={selectedPractitioner.contactEmail}
                        icon={
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-[#314e4a]/70 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                        }
                      />
                    )}
                  </div>
                </div>
              ) : (
              <div className="grid grid-cols-7 gap-1">
                {DAYS.map(day => {
                  const shift = selectedPractitioner.days_working[day];
                  const shiftStyles = getShiftStyles(shift);
                  return (
                    <div
                      key={day}
                      className="flex flex-col items-center p-1 rounded shadow-sm"
                      style={shiftStyles}
                    >
                      <span className="font-medium text-xs">{day}</span>
                      <span className="text-xs">{shift}</span>
                    </div>
                  );
                })}
              </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <h4 className="text-sm font-semibold text-[#314e4a] mb-2">Quick Info:</h4>
                <div className="bg-[#314e4a]/5 rounded-lg border border-[#314e4a]/10 p-3 text-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-[#314e4a]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                    </svg>
                    <span className="text-gray-600">Bed Height From Bottom:</span>
                    <span className="font-semibold text-[#314e4a]">{selectedPractitioner.bedHeight}</span>
                  </div>
                  {selectedPractitioner.title && (
                    <div className="flex items-start gap-2">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-[#314e4a] mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                      <div>
                        <span className="text-gray-600 block text-xs uppercase tracking-wide">Specialty</span>
                        <span className="font-semibold text-[#314e4a] leading-tight block">{selectedPractitioner.title}</span>
                      </div>
                    </div>
                  )}
                </div>
                {!selectedPractitioner.isByRequest && (selectedPractitioner.contactPhone || selectedPractitioner.contactEmail) && (
                  <div className="mt-3">
                    <h4 className="text-sm font-semibold text-[#314e4a] mb-2">Contact:</h4>
                    <div className="bg-[#314e4a]/5 rounded-lg border border-[#314e4a]/10 p-3 text-sm space-y-2">
                      {selectedPractitioner.contactPhone && (
                        <CopyableContact
                          value={selectedPractitioner.contactPhone}
                          icon={
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-[#314e4a]/70 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                            </svg>
                          }
                        />
                      )}
                      {selectedPractitioner.contactEmail && (
                        <CopyableContact
                          value={selectedPractitioner.contactEmail}
                          icon={
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-[#314e4a]/70 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                          }
                        />
                      )}
                    </div>
                  </div>
                )}
              </div>

              {getSpecialtyCategory(specialties, selectedPractitioner.specialty) === 'Acupuncture' && selectedPractitioner.insuranceNetworks && selectedPractitioner.insuranceNetworks.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-[#314e4a] mb-2">In-network with:</h4>
                  <div className="bg-[#314e4a]/5 rounded-lg border border-[#314e4a]/10 p-3">
                    <div className="flex flex-wrap gap-1.5">
                      {[...selectedPractitioner.insuranceNetworks].sort((a, b) => String(a).localeCompare(String(b), undefined, { sensitivity: 'base' })).map((network, i) => (
                        <span key={i} className="px-2.5 py-1 bg-white border border-[#314e4a]/20 text-[#314e4a] rounded-md text-xs font-medium shadow-sm">
                          {network}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        {activePracTab === 'services' && (
          <div className="p-3">
            {(primaryOfferedServices.length > 0 || addonOfferedServices.length > 0) ? (
              <>
                {primaryOfferedServices.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-1">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-teal-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                      Services Currently Offered
                    </h4>
                    <div className="space-y-1">
                      {primaryOfferedServices.map((osEntry, i) => {
                        const service = activeServices.find(s => s.name === osEntry.serviceName);
                        const isEven = i % 2 === 0;
                        return (
                          <div
                            key={`primary-os-${i}`}
                            className={`px-3 py-2.5 rounded-lg hover:bg-gray-50 cursor-pointer transition-all duration-200 border border-gray-200
                                                  ${isEven ? 'bg-white' : 'bg-slate-50/70'}`}
                            onClick={() => {
                              playClickSound();
                              if (service) {
                                setSelectedCategory(service.category);
                                setSelectedService(service);
                                const selectedCatObj = categories.find(cat => cat.name === service.category);
                                if (selectedCatObj) setMainBgColor(selectedCatObj.color);
                                else setMainBgColor(defaults.categoryColor || '#314e4a');
                              }
                            }}
                          >
                            <div className="flex justify-between items-start gap-3">
                              <div className="min-w-0 flex-1">
                                <div className="font-medium text-[13px] leading-5 text-gray-900 tracking-tight">{osEntry.serviceName}</div>
                                {osEntry.specialNote && (
                                  <p className="text-rose-600 text-xs mt-0.5 leading-4">{osEntry.specialNote}</p>
                                )}
                              </div>
                              <div className="flex items-center gap-1.5 flex-shrink-0 pt-0.5">
                                {validOfferedDurations(osEntry, activeServices.find(s => s.name === osEntry.serviceName)).map(d => (
                                  <span key={d} className="text-xs leading-none bg-teal-50 text-teal-700 font-semibold px-2 py-1 rounded-md shadow-sm">
                                    {d}m
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {addonOfferedServices.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-1">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-teal-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Add-on Services
                    </h4>
                    <div className="space-y-1">
                      {addonOfferedServices.map((osEntry, i) => {
                        const service = activeServices.find(s => s.name === osEntry.serviceName);
                        const isEven = i % 2 === 0;
                        return (
                          <div
                            key={`addon-os-${i}`}
                            className={`px-3 py-2.5 rounded-lg hover:bg-gray-50 cursor-pointer transition-all duration-200 border border-gray-200
                                                  ${isEven ? 'bg-white' : 'bg-slate-50/70'}`}
                            onClick={() => {
                              playClickSound();
                              if (service) {
                                setSelectedCategory(service.category);
                                setSelectedService(service);
                                const selectedCatObj = categories.find(cat => cat.name === service.category);
                                if (selectedCatObj) setMainBgColor(selectedCatObj.color);
                                else setMainBgColor(defaults.categoryColor || '#314e4a');
                              }
                            }}
                          >
                            <div className="flex justify-between items-start gap-3">
                              <div className="min-w-0 flex-1">
                                <div className="font-medium text-[13px] leading-5 text-gray-900 tracking-tight">
                                  {osEntry.serviceName.replace(ADDON_CLEANUP_REGEX, '').trim()}
                                </div>
                                {osEntry.specialNote && (
                                  <p className="text-rose-600 text-xs mt-0.5 leading-4">{osEntry.specialNote}</p>
                                )}
                              </div>
                              <div className="flex items-center gap-1.5 flex-shrink-0 pt-0.5">
                                {validOfferedDurations(osEntry, activeServices.find(s => s.name === osEntry.serviceName)).map(d => (
                                  <span key={d} className="text-xs leading-none bg-teal-50 text-teal-700 font-semibold px-2 py-1 rounded-md shadow-sm">
                                    {d}m
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-8">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-gray-500 font-medium mb-1">No services currently offered</p>
                <p className="text-sm text-gray-400">Services will be added soon.</p>
              </div>
            )}
          </div>
        )}
        {activePracTab === 'bio' && (
          <div className="p-6">
            {selectedPractitioner.description ? (
              <div
                className="prose prose-teal max-w-none prose-headings:text-gray-900 prose-headings:font-semibold prose-p:text-gray-600 prose-a:text-teal-600"
                style={{ textAlign: (selectedPractitioner.descriptionAlign || 'left') }}
                dangerouslySetInnerHTML={{ __html: cleanDescriptionHtml(selectedPractitioner.description) }}
              />
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}
